import { Award } from 'lucide-react'
import { Helmet } from 'react-helmet'
import { useLocation, useNavigate } from 'react-router'

import { OnboardingBannerSlot } from '@/components/OnboardingBannerSlot'
import { Banner } from '@/components/ui/banner'
import { GeneratedBlankState } from '@/components/ui/generated-blank-state'
import { SkeletonRows } from '@/components/ui/skeleton'
import { UnderlineTabs } from '@/components/ui/UnderlineTabs'
import { useCurrentCOIDocuments, useLatestCOIDocument } from '../../../../models/coi'
import { type Org, useCurrentUser } from '../../../../models/user'
import { CertificateHoldersTab } from './CertificateHoldersTab'
import { COIDocumentsTab } from './COIDocumentsTab'
import { COIEmailsTab } from './COIEmailsTab'
import { COIHeader } from './COIHeader'
import { COISentLogTab } from './COISentLogTab'

const DEFAULT_TAB = 'holders'

export function Certificates({
  org: orgProp,
  activeTab,
  onTabChange: onTabChangeProp,
}: {
  org?: Org
  activeTab?: string
  onTabChange?: (value: string) => void
}) {
  const { user, isLoading: userLoading } = useCurrentUser({ enabled: !orgProp })
  const org = orgProp ?? user?.currentOrg
  const orgId = orgProp?.id
  const isCaiAdmin = !!user?.isCaiAdmin
  const manualMode = !!org?.coiManualGeneration
  // Holders on a manual policy are added and their document uploaded by hand, so
  // that flow works before anything is generated. An org running both modes must
  // keep it reachable while an automatic policy still waits for its policy
  // document — that generates nothing, and used to hide the whole page.
  const hasManualPolicy = !!org?.coiHasManualPolicy
  // Every certificate the org holds, not just the headline one: with two
  // automatic policies only one of which has a document, the combined
  // certificate does not exist while the single-policy one does.
  const { documents, isLoading: documentsLoading } = useCurrentCOIDocuments(orgId)
  const { coiDocument, isLoading: latestLoading } = useLatestCOIDocument(orgId)
  const isLoading = userLoading || documentsLoading || latestLoading
  const location = useLocation()
  const navigate = useNavigate()
  // Generated certificates are ours to run; customers reach theirs from the header.
  const tabs = [
    { value: 'holders', label: 'Certificate holders' },
    ...(isCaiAdmin ? [{ value: 'documents', label: 'COI documents' }] : []),
    { value: 'sent-log', label: 'Sent log' },
    ...(isCaiAdmin ? [{ value: 'emails', label: 'Emails' }] : []),
  ]
  const requestedTab = activeTab ?? (location.hash.slice(1) || DEFAULT_TAB)
  const currentTab = tabs.some((tab) => tab.value === requestedTab) ? requestedTab : DEFAULT_TAB

  function onTabChange(value: string) {
    if (onTabChangeProp) {
      onTabChangeProp(value)
      return
    }
    navigate(
      { search: location.search, hash: value === DEFAULT_TAB ? '' : value },
      { replace: true, preventScrollReset: true }
    )
  }

  function renderContent() {
    if (isLoading) {
      return <SkeletonRows rows={3} />
    }

    if (!documents.length && !hasManualPolicy) {
      return (
        <GeneratedBlankState icon={Award}>
          Once your insurance policy is active, your COI will be generated automatically.
        </GeneratedBlankState>
      )
    }

    return (
      <div className="flex flex-col gap-8">
        {manualMode && (
          <Banner>
            This insurance carrier does not allow us as a broker to generate the COI, so requests may be a bit
            delayed.
          </Banner>
        )}
        <COIHeader email={coiDocument?.requestEmail} orgId={orgId} manualMode={manualMode} />

        <UnderlineTabs value={currentTab} onValueChange={onTabChange} tabs={tabs} />

        <div>
          {currentTab === 'holders' && <CertificateHoldersTab orgId={orgId} manualMode={manualMode} />}
          {currentTab === 'documents' && <COIDocumentsTab orgId={orgId} />}
          {currentTab === 'sent-log' && <COISentLogTab orgId={orgId} />}
          {currentTab === 'emails' && <COIEmailsTab orgId={orgId} />}
        </div>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>Certificates (COI)</title>
      </Helmet>
      <h1 className="text-ink mb-4 text-2xl font-bold tracking-[-0.01em]">Certificates (COI)</h1>
      <OnboardingBannerSlot className="mb-4" />
      {renderContent()}
    </>
  )
}
